/*
 * Shared derivations for the profile section.
 *
 * The order-history endpoint selects only `user items totalDeliveryCost
 * overallTotal status`, so anything else shown on these pages is derived
 * from what that response already carries rather than asked of the API.
 */

/* A Mongo ObjectId starts with a 4-byte creation timestamp, which is the
 * only date available once `createdAt` is left out of the projection. */
export const idToDate = (id) => {
  const hex = String(id || "").slice(0, 8);
  if (!/^[0-9a-f]{8}$/i.test(hex)) return null;
  return new Date(parseInt(hex, 16) * 1000);
};

/* Short, readable reference. `customFullOrderId` is not in the projection,
 * so the tail of the id stands in for it. */
export const orderRef = (id) => `#${String(id || "").slice(-6).toUpperCase()}`;

export const formatDate = (date) =>
  date
    ? date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const CLOSED = ["Delivered", "Cancelled", "cancelled", "rejected"];

export const isActive = (status) => !CLOSED.includes(status);

const STATUS_LABELS = {
  confirmed: "Confirmed",
  processing: "Processing",
  Dispatch: "Dispatched",
  Delivered: "Delivered",
  Cancelled: "Cancelled",
  cancelled: "Cancelled",
};

export const statusLabel = (status) =>
  STATUS_LABELS[status] || String(status || "—");

export const statusTone = (status) => {
  if (status === "Delivered") return "done";
  if (CLOSED.includes(status)) return "closed";
  if (status === "Dispatch") return "transit";
  if (status === "processing") return "active";
  return "wait";
};

/* Item count across an order's lines. */
export const itemCount = (order) =>
  (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);

/*
 * The tabs on the order history page. Counts come from the same list the
 * table renders, so they never disagree with what's on screen.
 */
export const ORDER_TABS = [
  { key: "all", label: "All Orders", match: () => true },
  { key: "active", label: "Active", match: (o) => isActive(o.status) },
  { key: "delivered", label: "Delivered", match: (o) => o.status === "Delivered" },
  {
    key: "cancelled",
    label: "Cancelled",
    match: (o) => ["Cancelled", "cancelled"].includes(o.status),
  },
];

/*
 * Address book. There is no address model, so the entries are the distinct
 * billing and delivery addresses the customer has actually checked out
 * with — real data, read-only, and each one keeps the order it came from.
 */
const joinAddress = (parts) => parts.filter(Boolean).join(", ");

export const addressesFromOrders = (orders) => {
  const seen = new Map();

  const add = (entry) => {
    if (!entry.lines) return;
    const key = entry.lines.toLowerCase();
    if (seen.has(key)) return;
    seen.set(key, entry);
  };

  orders.forEach((order) => {
    const billing = order.billingInfo || {};
    const delivery = order.deliveryInfo || {};

    add({
      id: `${order._id}-billing`,
      label: "Billing",
      name: joinAddress([billing.firstName, billing.lastName]).replace(", ", " "),
      lines: joinAddress([
        billing.streetAddress,
        billing.apartment,
        billing.city,
        billing.state,
        billing.postalCode,
        billing.country,
      ]),
      phone: billing.phone,
      orderId: order._id,
    });

    add({
      id: `${order._id}-delivery`,
      label: "Delivery",
      name: delivery.DeliveryFullName,
      lines: joinAddress([
        delivery.DeliveryStreetAddress,
        delivery.DeliveryApartment,
        delivery.DeliveryCity,
        delivery.DeliveryState,
        delivery.DeliveryPostalCode,
      ]),
      phone: delivery.DeliveryPhone,
      orderId: order._id,
    });
  });

  return [...seen.values()];
};

/* Initials tile stands in for the avatar the user record has no field for. */
export const initials = (name) =>
  String(name || "?")
    .replace(/@.*$/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "?";
